if (!customElements.get('product-card-carousel')) {
  customElements.define(
    'product-card-carousel',
    class ProductCardCarousel extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-product-card-carousel-list]');
        this.cards = Array.from(this.querySelectorAll('[data-product-card-carousel-card]'));
        this.dotsWrapper = this.querySelector('[data-product-card-carousel-dots]');
        this.dots = [];
        this.scrollPositions = [];
        this.selectedPageIndex = 0;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.dotsWrapper?.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-product-card-carousel-dot]');
          if (!dot || !this.dotsWrapper.contains(dot)) return;
          this.goToPage(Number(dot.dataset.productCardCarouselDot));
        }, { signal });

        this.list?.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => {
          this.buildDots();
          this.requestUpdate();
        }, { passive: true, signal });

        this.addEventListener('shopify:block:select', (event) => {
          const card = event.target.closest?.('[data-product-card-carousel-card]');
          const index = this.cards.indexOf(card);
          if (index >= 0) this.goTo(index, 'auto');
        }, { signal });

        this.buildDots();
        this.update();
        this.alignFeatures();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      get activeIndex() {
        if (!this.list || !this.cards.length) return 0;
        const listLeft = this.list.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - listLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      get activePage() {
        if (!this.list || !this.cards.length) return 0;
        const currentScroll = Math.round(this.list.scrollLeft);
        const matchingIndexes = this.scrollPositions
          .map((position, index) => ({ position, index }))
          .filter(({ position }) => Math.abs(currentScroll - Math.round(position)) <= 2)
          .map(({ index }) => index);

        if (matchingIndexes.includes(this.selectedPageIndex)) {
          return this.selectedPageIndex;
        }

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.scrollPositions.forEach((position, index) => {
          const distance = Math.abs(this.list.scrollLeft - position);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      goTo(index, behavior) {
        if (!this.list || !this.cards.length) return;
        const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
        const target = this.cards[targetIndex];
        if (!target) return;
        const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);

        this.list.scrollTo({
          left: Math.min(target.offsetLeft - this.list.offsetLeft, maxScroll),
          behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
        });
      }

      goToPage(index, behavior) {
        if (!this.list || !this.cards.length) return;
        const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
        this.selectedPageIndex = targetIndex;
        this.update();

        this.list.scrollTo({
          left: this.getCardScrollPosition(this.cards[targetIndex]),
          behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
        });
      }

      getCardScrollPosition(card) {
        if (!this.list || !card) return 0;
        const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
        return Math.min(card.offsetLeft - this.list.offsetLeft, maxScroll);
      }

      buildDots() {
        if (!this.list || !this.dotsWrapper) return;
        this.scrollPositions = this.cards.map((card) => this.getCardScrollPosition(card));

        this.dotsWrapper.replaceChildren();
        this.cards.forEach((card, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = this.dotsWrapper.dataset.dotClass || 'product-card-carousel__dot';
          dot.dataset.productCardCarouselDot = String(index);
          dot.setAttribute('aria-label', `Go to product carousel slide ${index + 1}`);
          this.dotsWrapper.append(dot);
        });

        this.dots = Array.from(this.dotsWrapper.querySelectorAll('[data-product-card-carousel-dot]'));
      }

      requestUpdate() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.update();
          this.alignFeatures();
        });
      }

      update() {
        if (!this.list) return;
        const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
        const canScroll = maxScroll > 2;
        const activePage = this.activePage;
        this.selectedPageIndex = activePage;

        if (this.dotsWrapper) this.dotsWrapper.hidden = !canScroll;
        this.dots.forEach((dot, index) => {
          const selected = index === activePage;
          dot.classList.toggle('is-active', selected);
          dot.setAttribute('aria-current', selected ? 'true' : 'false');
        });
      }

      alignFeatures() {
        if (!this.cards.length) return;
        const featureRows = this.cards.map((card) => Array.from(card.querySelectorAll('[data-product-card-carousel-feature]')));
        const maxRows = Math.max(...featureRows.map((rows) => rows.length));

        featureRows.flat().forEach((feature) => {
          feature.style.height = 'auto';
        });

        for (let i = 0; i < maxRows; i += 1) {
          const rowFeatures = featureRows.map((rows) => rows[i]).filter(Boolean);
          if (rowFeatures.length < 2) continue;
          const maxHeight = Math.max(...rowFeatures.map((feature) => feature.offsetHeight));
          rowFeatures.forEach((feature) => {
            feature.style.height = `${maxHeight}px`;
          });
        }
      }
    }
  );
}
